import { api } from '../../core/network/api';
import { startArena, type FightView } from './mobfight-api';
import type { HeroClass } from './hero-api';

/** Begin (or resume) an arena bout (the Holmgang) — same shared session as
 *  mob/raid fights; drive it with fightRound/fightHeal/fightAbandon. */
export const startArenaFight = startArena;
export type { FightView };

/** The concrete bot opponent held pending after a search — matchmaking
 *  synthesizes them up front so the "Opponent found" card can show WHO. */
export interface ArenaOpponent {
  name: string;
  heroClass: HeroClass;
  level: number;
  rating: number;
  /** What THIS bout moves the caller's rating by, win and lose — the exact
   *  deltas the settlement will apply, so the terms can quote the number the
   *  player will actually watch change (QA 86cb8zdku). Signed. */
  winRating: number;
  lossRating: number;
  /** Epoch ms the server stops accepting this hold. The card counts down to it
   *  and returns itself to the lobby when it passes — before this landed the
   *  card looked live forever and FIGHT bounced with "No opponent found"
   *  (QA 86cb9yd3p item 6). */
  expiresAt: number;
  /** Epoch ms the foe is revealed — the end of the queue wait, and the instant
   *  the cancellation fee starts applying. Rolled by the SERVER (15-60s): the
   *  fee hangs off it, so a client-rolled wait would let anyone cancel for free
   *  by claiming they had not looked yet. */
  revealAt: number;
}

/** Search for a bout. Fast (bot-only matchmaking) — does NOT spend fury and
 *  does NOT start the fight, it just holds the match pending server-side
 *  until accept/cancel. 400s with "Not enough fury (need N)." if the hero
 *  can't afford a bout. */
export const searchArena = () => api.post<ArenaOpponent>('/arena/search');

/** Accept the pending match: spends the fury and starts the SAME interactive
 *  bout `startArena`/TacticalBattle drives (400s "No opponent found — search
 *  again." if the hold expired or was never made). */
export const acceptArena = () => api.post<FightView>('/arena/accept');

/** Drop the pending match. Idempotent, never spends fury — but dropping a foe
 *  already REVEALED costs rating (CU-12418agekkx item 2; it was silver before).
 *  `ratingLost` is what was actually taken: 0 while the foe is still hidden,
 *  once the hold has lapsed on its own, or when there was nothing pending;
 *  floored at 0 rating otherwise. `rating` is the standing after the penalty
 *  (null when nothing was charged). Quote `ratingLost`, not the constant. */
export const cancelArena = () =>
  api.post<{ cancelled: true; ratingLost: number; rating: number | null }>('/arena/cancel');

/** One row in the recent-bouts ledger (GET /arena/stats). */
export interface ArenaRecentBout {
  result: 'VICTORY' | 'DEFEAT';
  opponent: string;
  ratingChange: number;
  at: string;
}

/** What a bout costs and pays — served from the balance constants so the
 *  lobby can state the terms without restating (and desyncing from) them. */
export interface ArenaStakes {
  nerveCost: number;
  winGold: number;
  lossGold: number;
  winRankPoints: number;
  lossRankPoints: number;
  /** The flat rating a win pays (positive) and a loss costs (negative) — the
   *  ladder numbers the terms quote (owner 2026-08-28: "standing" beside them
   *  read as useless noise). Asymmetric since 02.09: +15 / −17. */
  winRating: number;
  lossRating: number;
  /** Rating taken for dropping a foe already revealed (positive: the points
   *  lost) — put on the Cancel button itself, so nobody discovers the price by
   *  paying it. */
  cancelRating: number;
}

/** The caller's arena standing — rating/title/W-L/streak + recent bouts. */
export interface ArenaStats {
  rating: number;
  title: string;
  /** Your own hero level — shown beside your title, mirroring the foe's plate. */
  level: number;
  wins: number;
  losses: number;
  streak: number;
  stakes: ArenaStakes;
  recent: ArenaRecentBout[];
}

export const fetchArenaStats = () => api.get<ArenaStats>('/arena/stats');
